import type { Location as StoreLocation } from "@hobbyshopify/backend-client";
import { useState } from "react";

/**
 * Step two of confirming a card: does the shop count copies of this one?
 *
 * Its own step rather than a checkbox on the form, because it is a different
 * kind of decision. Step one is what the card *is* and what it is worth — the
 * identity and the floor. This is whether the shop wants to keep a count, and
 * for a lot of a shop's floor the honest answer is no: singles nobody wants to
 * inventory, priced and sold without a ledger.
 *
 * Both answers are a confirm. Skipping is not a cancel, and the wording says so —
 * the card is created either way.
 */
export function StockTrackingModal({
  locations,
  busy,
  onTrack,
  onSkip,
  onCancel,
}: {
  // Offered inside this dialog rather than as a third step: a merchant who has
  // just said "track 3" should not then be asked a separate question about
  // where they are.
  locations: StoreLocation[];
  busy?: boolean;
  onTrack: (opts: { quantity: number; locationId?: string }) => void;
  onSkip: () => void;
  onCancel: () => void;
}) {
  const [quantity, setQuantity] = useState("1");
  const [locationId, setLocationId] = useState<string>(locations[0]?.id ?? "");

  const parsed = Number(quantity);
  const quantityValid = Number.isInteger(parsed) && parsed >= 1;

  return (
    <div className="scrim items-end justify-center p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="stock-tracking-title"
        className="modal relative w-full max-w-sm p-6"
      >
        <h2 id="stock-tracking-title" className="mb-1 text-base font-semibold text-white">
          Count stock for this card?
        </h2>
        <p className="mb-4 text-sm text-muted">
          Counting keeps a copy count and holds one when a customer is quoted. Without it the card still sells; it just
          never shows as out of stock.
        </p>

        <div className="space-y-3">
          <div>
            <div className="mb-1 block text-xs font-medium text-muted">Quantity</div>
            <input
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              className={`field ${quantityValid ? "" : "border-danger focus:border-danger"}`}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              aria-invalid={!quantityValid}
            />
            {!quantityValid && <p className="field-error text-xs">Enter a whole number of at least 1.</p>}
          </div>

          {locations.length > 1 && (
            <div>
              <div className="mb-1 block text-xs font-medium text-muted">Location</div>
              <select className="field" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="mt-6 flex gap-3">
          {/* Skipping confirms the card too — it only declines the counting. Secondary
              rather than ghost so it never reads as the way out of the dialog. */}
          <button type="button" onClick={onSkip} disabled={busy} className="btn-secondary flex-1">
            Add without count
          </button>
          <button
            type="button"
            onClick={() => onTrack({ quantity: parsed, ...(locations.length > 1 && locationId ? { locationId } : {}) })}
            disabled={busy || !quantityValid}
            className="btn-primary flex-1"
          >
            {busy ? "Confirming…" : "Count stock"}
          </button>
        </div>

        <button type="button" onClick={onCancel} disabled={busy} className="btn-ghost mt-3 w-full">
          Back to review
        </button>
      </div>
    </div>
  );
}
